// src/modules/purityAnalyzer/pages/RunTest.jsx
// Gold Purity Analysis — sci-fi instrument console. The HUD display sits on
// top (standby until the operator presses ANALYZE); the Sample Weighing input
// console and optional customer/transaction links sit below it.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Form, InputNumber, Select, Input, Button, Row, Col, Typography,
  message, Alert, Divider, Spin, Switch, Modal, Space,
} from 'antd';
import {
  ExperimentOutlined, SaveOutlined, GoldOutlined, ThunderboltOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import debounce from '../utils/debounce';
import ResultInstrument from '../components/ResultInstrument';
import StatusLed from '../components/hud/StatusLed';
import {
  previewTest, runTest, fetchMachines,
  searchCustomers, searchWalkInCases, searchPledgeCases,
} from '../api';
import {
  TRANSACTION_KIND, TRANSACTION_KIND_LABELS, PURITY_CATEGORY, PURITY_CATEGORY_LABELS,
} from '../constants/purityConstants';
import '../components/scifi.css';

const { Title, Text } = Typography;

// Fields whose change invalidates a previously computed analysis.
const ANALYSIS_FIELDS = ['goldWeightAir', 'goldWeightDiesel', 'machineId', 'purityCategory'];

// Standby console shown until an analysis has been run.
function StandbyConsole({ error, analyzing }) {
  return (
    <div className="pa-console" style={{ padding: 18, minHeight: 260 }}>
      <div style={{
        position: 'relative', zIndex: 2, display: 'flex', flexWrap: 'wrap',
        gap: 18, alignItems: 'center', justifyContent: 'space-between',
      }}>
        <StatusLed color="#52e5a3" label="System Online" />
        <span className="pa-mono pa-label" style={{ fontSize: 11 }}>AU·ARCHIMEDES·STANDBY</span>
      </div>
      <div style={{
        position: 'relative', zIndex: 2, textAlign: 'center',
        padding: '48px 16px 40px',
      }}>
        <motion.div
          animate={{ opacity: analyzing ? [1, 0.4, 1] : 1 }}
          transition={{ duration: 0.9, repeat: analyzing ? Infinity : 0 }}
        >
          <ExperimentOutlined style={{ fontSize: 46, color: 'rgba(0,229,255,0.5)' }} />
        </motion.div>
        <div className="pa-mono" style={{
          marginTop: 18, fontSize: 20, letterSpacing: '0.3em',
          color: analyzing ? '#ffd166' : error ? '#ff6b6b' : 'rgba(0,229,255,0.85)',
        }}>
          {analyzing ? 'ANALYZING SAMPLE…' : error ? 'INPUT FAULT' : 'AWAITING SAMPLE'}
        </div>
        <div className="pa-label" style={{ marginTop: 10 }}>
          {error || 'Enter the sample weights below, then press ANALYZE'}
        </div>
        <div className="pa-standby-border" style={{ height: 1, maxWidth: 420, margin: '26px auto 0' }} />
      </div>
    </div>
  );
}

export default function RunTest() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [machines, setMachines] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [txnOptions, setTxnOptions] = useState([]);
  const [txnKind, setTxnKind] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [displayMode, setDisplayMode] = useState('fullscreen');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const previewSeq = useRef(0);

  useEffect(() => {
    fetchMachines({ status: 'ACTIVE' })
      .then((res) => setMachines(res?.data?.data || []))
      .catch(() => setMachines([]));
  }, []);

  // ── Analyze (explicit — no auto-calculation while typing) ──
  const onAnalyze = async () => {
    try {
      await form.validateFields(['goldWeightAir', 'goldWeightDiesel']);
    } catch {
      return; // validation messages shown inline
    }
    const { goldWeightAir, goldWeightDiesel, machineId, purityCategory } = form.getFieldsValue();
    if (goldWeightDiesel >= goldWeightAir) {
      setPreview(null);
      setPreviewError('Weight in diesel must be less than weight in air');
      return;
    }
    const seq = ++previewSeq.current;
    setAnalyzing(true);
    setPreviewError(null);
    try {
      const res = await previewTest({ goldWeightAir, goldWeightDiesel, machineId, purityCategory });
      if (seq !== previewSeq.current) return;
      setPreview(res?.data?.data || null);
      if (displayMode === 'fullscreen') setFullscreenOpen(true);
    } catch (err) {
      if (seq !== previewSeq.current) return;
      setPreview(null);
      setPreviewError(err?.response?.data?.message || 'Analysis failed');
    } finally {
      if (seq === previewSeq.current) setAnalyzing(false);
    }
  };

  // Changing any analysis input invalidates the current result so a stale
  // reading can never be saved against new weights.
  const onValuesChange = (changed) => {
    if (preview && ANALYSIS_FIELDS.some((f) => f in changed)) {
      setPreview(null);
      setPreviewError(null);
    }
  };

  const onReset = () => {
    form.resetFields();
    setPreview(null);
    setPreviewError(null);
    setTxnKind(null);
    setTxnOptions([]);
  };

  // ── Lookups ──
  const doCustomerSearch = useMemo(
    () =>
      debounce(async (q) => {
        if (!q || q.length < 2) return;
        try {
          const res = await searchCustomers(q);
          const payload = res?.data?.data || res?.data || {};
          const items = payload.customers || payload.items || (Array.isArray(payload) ? payload : []);
          setCustomerOptions(
            items.map((c) => ({
              value: c._id,
              label: `${c.name || 'Unnamed'}${c.phone ? ` · ${c.phone}` : ''}`,
            }))
          );
        } catch { /* lookup failures shouldn't block a test */ }
      }, 400),
    []
  );

  const doTxnSearch = useMemo(
    () =>
      debounce(async (q, kind) => {
        if (!kind || !q || q.length < 2) return;
        try {
          const fn = kind === TRANSACTION_KIND.WALK_IN_CASE ? searchWalkInCases : searchPledgeCases;
          const res = await fn(q);
          const payload = res?.data?.data || res?.data || {};
          const items = payload.cases || payload.items || (Array.isArray(payload) ? payload : []);
          setTxnOptions(
            items.map((c) => ({
              value: c._id,
              label: c.caseId || c.caseNumber || c._id,
            }))
          );
        } catch { /* optional linkage */ }
      }, 400),
    []
  );

  // ── Save ──
  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const body = {
        goldWeightAir: values.goldWeightAir,
        goldWeightDiesel: values.goldWeightDiesel,
        machineId: values.machineId,
        purityCategory: values.purityCategory,
        customerId: values.customerId || null,
        sampleDescription: values.sampleDescription,
        notes: values.notes,
      };
      if (values.transactionKind) {
        const opt = txnOptions.find((o) => o.value === values.transactionId);
        body.transactionRef = {
          kind: values.transactionKind,
          id: values.transactionId || null,
          label: opt?.label || values.transactionLabel || null,
        };
      }
      const res = await runTest(body);
      const test = res?.data?.data;
      message.success(`Test ${test?.testNumber} saved`);
      navigate(`/purity/tests/${test?._id}`);
    } catch (err) {
      if (err?.errorFields) return; // form validation
      message.error(err?.response?.data?.message || 'Failed to save test');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ marginTop: 0, marginBottom: 0 }}>
          <ExperimentOutlined style={{ marginRight: 10 }} />
          Gold Purity Analysis
        </Title>
        <Space>
          <Text style={{ fontSize: 12 }}>Display:</Text>
          <Switch
            checkedChildren="Fullscreen"
            unCheckedChildren="Inline"
            checked={displayMode === 'fullscreen'}
            onChange={(checked) => setDisplayMode(checked ? 'fullscreen' : 'inline')}
          />
        </Space>
      </div>

      {/* ── Instrument display (top) ── */}
      {preview ? (
        displayMode === 'inline' ? (
          <ResultInstrument
            live
            result={preview}
            context={{ machine: preview.machine }}
            animateKey={`${preview.goldDensity}-${preview.predictedPurity}`}
          />
        ) : (
          <div className="pa-console" style={{ padding: 40, textAlign: 'center', minHeight: 260, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <StatusLed color="#52e5a3" label="Analysis Complete" />
            <div className="pa-mono" style={{ fontSize: 20, color: '#52e5a3', margin: '18px 0 24px', letterSpacing: '0.2em' }}>
              ANALYSIS READY
            </div>
            <Button type="primary" size="large" onClick={() => setFullscreenOpen(true)} style={{ width: 220, height: 50, fontSize: 18, letterSpacing: '0.1em' }}>
              VIEW RESULT
            </Button>
          </div>
        )
      ) : (
        <StandbyConsole error={previewError} analyzing={analyzing} />
      )}

      {/* ── Sample Weighing console (below the display) ── */}
      <Form
        form={form}
        layout="vertical"
        onValuesChange={onValuesChange}
      >
        <Card
          style={{ marginTop: 24, borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.05)', border: '1px solid #f0f0f0' }}
          title={<span style={{ fontSize: 18, fontWeight: 600 }}><GoldOutlined style={{ marginRight: 10, color: '#d4af37' }} />Sample Weighing</span>}
          extra={
            <Space size="middle" align="center">
              <Form.Item
                name="purityCategory"
                initialValue={PURITY_CATEGORY.HM}
                valuePropName="checked"
                getValueProps={(value) => ({ checked: value === PURITY_CATEGORY.HM })}
                getValueFromEvent={(checked) => (checked ? PURITY_CATEGORY.HM : PURITY_CATEGORY.NON_HM)}
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checkedChildren="HM"
                  unCheckedChildren="Non-HM"
                  title={PURITY_CATEGORY_LABELS.HM}
                  size="small"
                />
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12, letterSpacing: '0.05em' }}>ARCHIMEDES</Text>
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item
                name="goldWeightAir"
                label={<Text strong>Gold Weight in Air (g)</Text>}
                rules={[{ required: true, message: 'Enter weight in air' }]}
              >
                <InputNumber
                  style={{ width: '100%', borderRadius: 6 }} min={0.0001} step={0.001}
                  precision={4} placeholder="e.g. 50.0000" autoFocus
                  onPressEnter={onAnalyze}
                />
              </Form.Item>

              <Form.Item
                name="goldWeightDiesel"
                label={<Text strong>Gold Weight in Diesel (g)</Text>}
                rules={[{ required: true, message: 'Enter weight in diesel' }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  style={{ width: '100%', borderRadius: 6 }} min={0.0001} step={0.001}
                  precision={4} placeholder="e.g. 47.8500"
                  onPressEnter={onAnalyze}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              {machines.length > 1 && (
                <Form.Item name="machineId" label={<Text strong>Machine</Text>}>
                  <Select
                    allowClear placeholder="Default machine"
                    options={machines.map((m) => ({
                      value: m._id, label: `${m.machineCode} · ${m.name}`,
                    }))}
                  />
                </Form.Item>
              )}
              
              <Form.Item label=" " colon={false} style={{ marginTop: machines.length > 1 ? 0 : 2 }}>
                <Button
                  type="primary" icon={<ThunderboltOutlined />}
                  onClick={onAnalyze} loading={analyzing}
                  style={{ width: 160, letterSpacing: '0.08em', fontWeight: 600, borderRadius: 6 }}
                >
                  ANALYZE
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Divider plain style={{ fontSize: 14, margin: '24px 0', fontWeight: 600, color: '#8c8c8c' }}>
            Sample & Optional Links
          </Divider>

          <Row gutter={[24, 24]} align="bottom">
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="sampleDescription" label={<Text strong>Sample Description</Text>}>
                <Input size="large" placeholder="e.g. Bangle, 1 pc" style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="customerId" label={<Text strong>Customer</Text>}>
                <Select
                  size="large"
                  showSearch allowClear placeholder="Search customer…"
                  filterOption={false} onSearch={doCustomerSearch}
                  options={customerOptions} notFoundContent={null}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="transactionKind" label={<Text strong>Transaction Type</Text>}>
                <Select
                  size="large"
                  allowClear placeholder="Type"
                  onChange={(v) => { setTxnKind(v); setTxnOptions([]); form.setFieldValue('transactionId', undefined); }}
                  options={Object.values(TRANSACTION_KIND).map((k) => ({
                    value: k, label: TRANSACTION_KIND_LABELS[k],
                  }))}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="transactionId" label={<Text strong>Case</Text>}>
                <Select
                  size="large"
                  showSearch allowClear placeholder={txnKind ? 'Search case…' : 'Type first'}
                  disabled={!txnKind} filterOption={false}
                  onSearch={(q) => doTxnSearch(q, txnKind)}
                  options={txnOptions} notFoundContent={null}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item name="notes" label={<Text strong>Notes</Text>}>
                <Input size="large" placeholder="Observations…" style={{ borderRadius: 6 }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={24} lg={8}>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary" size="large" block icon={<SaveOutlined />}
                  onClick={onSave} loading={saving} disabled={!preview}
                  title={preview ? undefined : 'Run ANALYZE first'}
                  style={{
                    height: 40,
                    letterSpacing: '0.05em',
                    fontWeight: 600,
                    borderRadius: 6,
                    background: preview ? 'linear-gradient(90deg, #1890ff, #36cfc9)' : undefined,
                    borderColor: 'transparent',
                    opacity: !preview ? 0.6 : 1
                  }}
                >
                  SAVE RESULT
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>

      {/* ── Fullscreen Result Modal ── */}
      <Modal
        title={null}
        footer={null}
        closable={false}
        open={fullscreenOpen}
        onCancel={() => setFullscreenOpen(false)}
        width="100vw"
        style={{ top: 0, padding: 0, margin: 0, maxWidth: '100vw' }}
        wrapClassName="pa-fullscreen-modal"
        styles={{
          body: { height: '100vh', overflowY: 'auto', backgroundColor: '#0b1319', padding: 0, margin: 0 },
          content: { padding: 0, margin: 0, backgroundColor: '#0b1319', borderRadius: 0, border: 'none' },
          mask: { backgroundColor: '#000' }
        }}
      >
        {preview && (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
              <Button danger onClick={() => setFullscreenOpen(false)}>
                Close & Return
              </Button>
            </div>
            <ResultInstrument
              live
              result={preview}
              context={{ machine: preview.machine }}
              animateKey={`${preview.goldDensity}-${preview.predictedPurity}-fs`}
            />
          </div>
        )}
      </Modal>

      {previewError && preview === null && (
        <Alert type="warning" showIcon message={previewError} style={{ marginTop: 12 }} />
      )}
      <style>{`
        .pa-fullscreen-modal .ant-modal-content {
          padding: 0 !important;
          border-radius: 0 !important;
          background-color: #0b1319 !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
}
